/**
 * Renders Shipping threshold feedback for the Retail cart. Validation state
 * comes from shippingUtils; this component owns currency, copy, and navigation.
 */

import type { Merchant } from "@/dto/merchant";
import {
  ShippingCartBannerState,
  type ShippingCartBannerState as ShippingCartBannerStateType,
} from "@/utils/shippingUtils";
import { ROUTE_ALL, ROUTE_SHOP } from "@/utils/constants";
import Link from "next/link";
import { useFormatter, useTranslations } from "next-intl";
import type { ReactNode } from "react";
import VariantBanner from "@/components/banners/variantBanner";

type ThresholdBannerState = Exclude<
  ShippingCartBannerStateType,
  typeof ShippingCartBannerState.UNSHIPPABLE | null
>;

interface ShippingCartBannerProps {
  state: ThresholdBannerState;
  minimumAmount: number | null;
  currency: string;
  merchant: Pick<
    Merchant,
    "isHybrid" | "isService" | "hasOnlyRetailItems"
  >;
  onAddMore: () => void;
}

export const ShippingCartBanner = ({
  state,
  minimumAmount,
  currency,
  merchant,
  onAddMore,
}: ShippingCartBannerProps) => {
  const t = useTranslations("ShippingCartBanner");
  const format = useFormatter();
  const catalogRoute =
    merchant.isHybrid || (merchant.isService && merchant.hasOnlyRetailItems)
      ? ROUTE_SHOP
      : ROUTE_ALL;

  if (state === ShippingCartBannerState.FREE_SHIPPING_UNLOCKED) {
    return (
      <VariantBanner variant="info">
        {t("freeShippingUnlocked")}
      </VariantBanner>
    );
  }

  if (minimumAmount === null) return null;

  const formattedMinimum = format.number(minimumAmount, {
    style: "currency",
    currency,
    currencyDisplay: "symbol",
  });
  const renderLink = (chunks: ReactNode) => (
    <Link
      href={catalogRoute}
      onClick={onAddMore}
      className="underline cursor-pointer text-[#0764AB]"
    >
      {chunks}
    </Link>
  );

  return (
    <VariantBanner
      title={t("minimumTitle", { amount: formattedMinimum })}
      variant="warning"
    >
      {t.rich("minimumBody", { link: renderLink })}
    </VariantBanner>
  );
};
